// 引入 React 常用 Hook。
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// 引入全局 action 类型与命名空间。
import { ActionTypeValues, namespace } from "@/context";
// 引入帖子列表项类型。
import { IThreadLisItem } from "@/api/thread/list";
// 引入通知消息接口与类型。
import { getNotificationList, INotificationItem } from "@/api/message";
// 引入用户中心作品接口。
import { getUserCenterThreads, USER_CENTER_THREAD_TYPE } from "@/api/userCenterThreads";
// 引入互动操作类型枚举。
import { USER_ACTION_TYPE } from "@/components/UserAction";
// 引入分页常量。
import { USER_CENTER_MESSAGE_PAGE_SIZE, USER_CENTER_PAGE_SIZE, USER_CENTER_REPLY_PAGE_SIZE } from "./constants";

// 引入我的评论（帖子回复列表）接口与类型。
import { getUserRepliesList, IUserReplyItem } from "@/api/userReplies";

// Hook 入参类型定义。
interface Params {
  initialWorks: IThreadLisItem[];
  initialUserInfo: any;
  account: any;
  userStats: any;
  displayAccount: any;
  isSelfView: boolean;
  activeTab: string;
  dispatch: (action: any) => void;
  threadStatePatchRef: React.MutableRefObject<Record<string, Partial<IThreadLisItem>>>;
}

// PC 用户中心列表逻辑 Hook（帖子/点赞/收藏/消息分页与本地状态同步）。
export const useUserCenterPcListLogic = ({
  initialWorks,
  initialUserInfo,
  account,
  userStats,
  displayAccount,
  isSelfView,
  activeTab,
  dispatch,
  threadStatePatchRef,
}: Params) => {
  // 帖子列表状态（首屏数据去重后初始化）。
  const [postsData, setPostsData] = useState<IThreadLisItem[]>(() => {
    const seen = new Set<string>();
    return initialWorks.reduce((acc, current) => {
      const key = String(current.tid);
      if (!seen.has(key)) {
        seen.add(key);
        return acc.concat([current]);
      }
      return acc;
    }, [] as IThreadLisItem[]);
  });
  const [postsLoading, setPostsLoading] = useState(false);
  // 首屏少于分页大小时视为已到底。
  const [postsHasMore, setPostsHasMore] = useState(() => {
    return initialWorks.length >= USER_CENTER_PAGE_SIZE;
  });
  const postsHasMoreRef = useRef(initialWorks.length >= USER_CENTER_PAGE_SIZE);
  const postsLoadInFlightRef = useRef(false);
  const [postsInitialized, setPostsInitialized] = useState(true);

  // 点赞列表状态。
  const [likesData, setLikesData] = useState<IThreadLisItem[]>([]);
  const [likesLoading, setLikesLoading] = useState(false);
  const [likesHasMore, setLikesHasMore] = useState(true);
  const likesHasMoreRef = useRef(true);
  const likesLoadInFlightRef = useRef(false);
  const [likesInitialized, setLikesInitialized] = useState(false);

  // 收藏列表状态。
  const [collectionsData, setCollectionsData] = useState<IThreadLisItem[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsHasMore, setCollectionsHasMore] = useState(true);
  const collectionsHasMoreRef = useRef(true);
  const collectionsLoadInFlightRef = useRef(false);
  const [collectionsInitialized, setCollectionsInitialized] = useState(false);

  // 消息列表状态。
  const [messagesData, setMessagesData] = useState<INotificationItem[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(true);
  const [messagesInitialized, setMessagesInitialized] = useState(false);

  // 我的评论列表状态。
  const [repliesData, setRepliesData] = useState<IUserReplyItem[]>([]);
  const repliesDataRef = useRef<IUserReplyItem[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [repliesHasMore, setRepliesHasMore] = useState(true);
  const [repliesInitialized, setRepliesInitialized] = useState(false);
  // page-based 接口需要显式维护当前页号，避免“列表长度不增长”导致一直请求同一页。
  const repliesPageRef = useRef(1);
  // 请求中锁：防止滚动事件在 setRepliesLoading 生效前重复触发。
  const repliesRequestInFlightRef = useRef(false);
  // 记录消息数据快照，避免异步闭包读旧值。
  const messagesDataRef = useRef<INotificationItem[]>([]);
  // 首屏帖子快照签名，避免重复重置 postsData。
  const postsSnapshotSignatureRef = useRef("");
  // 消息刷新并发锁，避免重复刷新未读逻辑。
  const messageRefreshInFlightRef = useRef(false);

  // 同步消息状态到 ref。
  useEffect(() => {
    messagesDataRef.current = messagesData;
  }, [messagesData]);

  // 同步我的评论状态到 ref，避免闭包导致 offset/page 计算滞后。
  useEffect(() => {
    repliesDataRef.current = repliesData;
  }, [repliesData]);

  // 当展示的用户变更时，重置“我的评论”分页状态。
  useEffect(() => {
    repliesPageRef.current = 1;
    setRepliesData([]);
    repliesDataRef.current = [];
    setRepliesInitialized(false);
    setRepliesHasMore(true);
  }, [displayAccount?.id, account?.id]);

  // 首屏帖子变化时重新构建 postsData 并叠加 patch。
  useEffect(() => {
    const seen = new Set<string>();
    const uniqueWorks = initialWorks.reduce((acc, current) => {
      const key = String(current.tid);
      if (!seen.has(key)) {
        seen.add(key);
        return acc.concat([current]);
      }
      return acc;
    }, [] as IThreadLisItem[]);
    const ownerId = String(initialUserInfo?.id || account?.id || "");
    const totalWorks = typeof userStats?.totalWorks === "number" ? userStats.totalWorks : -1;
    const worksSignature = uniqueWorks.map((item) => String(item.tid)).join(",");
    const nextSnapshotSignature = `${ownerId}|${totalWorks}|${worksSignature}`;
    if (postsSnapshotSignatureRef.current === nextSnapshotSignature) return;
    postsSnapshotSignatureRef.current = nextSnapshotSignature;

    const patchedUniqueWorks = uniqueWorks.map((item) => {
      const patch = threadStatePatchRef.current[String(item.tid)];
      return patch ? { ...item, ...patch } : item;
    });

    setPostsData(patchedUniqueWorks);
    const nextPostsHasMore = uniqueWorks.length >= USER_CENTER_PAGE_SIZE;
    postsHasMoreRef.current = nextPostsHasMore;
    setPostsHasMore(nextPostsHasMore);
    setPostsInitialized(true);
    postsLoadInFlightRef.current = false;
    setPostsLoading(false);
  }, [initialWorks, userStats?.totalWorks, initialUserInfo?.id, account?.id, threadStatePatchRef]);

  // 加载更多帖子。
  const loadMorePosts = useCallback(async () => {
    if (postsLoading || !postsHasMoreRef.current || postsLoadInFlightRef.current) return;
    postsLoadInFlightRef.current = true;
    setPostsLoading(true);
    try {
      const offset = postsData.length;
      const page = offset === 0 ? 1 : Math.floor((offset - 1) / USER_CENTER_PAGE_SIZE) + 2;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setPostsLoading(false);
        return;
      }
      const res = await getUserCenterThreads({
        uid: Number(uid),
        type: USER_CENTER_THREAD_TYPE.MY_POSTS,
        page,
        page_size: USER_CENTER_PAGE_SIZE,
      });
      const newItems = (res.items || []).map((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return patch ? { ...item, ...patch } : item;
      });
      const returnedCount = res.items?.length || 0;
      setPostsData((prev) => {
        const existingIds = new Set(prev.map((item) => String(item.tid)));
        const uniqueNewItems: IThreadLisItem[] = [];
        for (const item of newItems) {
          const key = String(item.tid);
          if (!existingIds.has(key)) {
            existingIds.add(key);
            uniqueNewItems.push(item);
          }
        }
        return [...prev, ...uniqueNewItems];
      });
      setPostsInitialized(true);
      const nextPostsHasMore = returnedCount >= USER_CENTER_PAGE_SIZE;
      postsHasMoreRef.current = nextPostsHasMore;
      setPostsHasMore(nextPostsHasMore);
    } catch (error) {
      console.error("Failed to load more posts:", error);
    } finally {
      postsLoadInFlightRef.current = false;
      setPostsLoading(false);
    }
  }, [postsLoading, postsData.length, displayAccount?.id, account?.id, threadStatePatchRef]);

  // 加载更多点赞。
  const loadMoreLikes = useCallback(async () => {
    if (likesLoading || !likesHasMoreRef.current || likesLoadInFlightRef.current) return;
    likesLoadInFlightRef.current = true;
    setLikesLoading(true);
    try {
      const offset = likesInitialized ? likesData.length : 0;
      const page = offset === 0 ? 1 : Math.floor((offset - 1) / USER_CENTER_PAGE_SIZE) + 2;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setLikesLoading(false);
        return;
      }
      const res = await getUserCenterThreads({
        uid: Number(uid),
        type: USER_CENTER_THREAD_TYPE.LIKED,
        page,
        page_size: USER_CENTER_PAGE_SIZE,
      });
      const patchedNewItems = (res.items || []).map((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return patch ? { ...item, ...patch } : item;
      });
      const validPatchedItems = patchedNewItems.filter((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return !(patch && patch.praised === false);
      });
      const returnedCount = res.items?.length || 0;
      setLikesData((prev) => {
        const existingIds = new Set(prev.map((item) => String(item.tid)));
        const uniqueNewItems: IThreadLisItem[] = [];
        for (const item of validPatchedItems) {
          const key = String(item.tid);
          if (!existingIds.has(key)) {
            existingIds.add(key);
            uniqueNewItems.push(item);
          }
        }
        return [...prev, ...uniqueNewItems];
      });
      setLikesInitialized(true);
      const nextLikesHasMore = returnedCount >= USER_CENTER_PAGE_SIZE;
      likesHasMoreRef.current = nextLikesHasMore;
      setLikesHasMore(nextLikesHasMore);
    } catch (error) {
      console.error("Failed to load more likes:", error);
    } finally {
      likesLoadInFlightRef.current = false;
      setLikesLoading(false);
    }
  }, [likesLoading, likesInitialized, likesData.length, displayAccount?.id, account?.id, threadStatePatchRef]);

  // 加载更多收藏。
  const loadMoreCollections = useCallback(async () => {
    if (collectionsLoading || !collectionsHasMoreRef.current || collectionsLoadInFlightRef.current) return;
    collectionsLoadInFlightRef.current = true;
    setCollectionsLoading(true);
    try {
      const offset = collectionsInitialized ? collectionsData.length : 0;
      const page = offset === 0 ? 1 : Math.floor((offset - 1) / USER_CENTER_PAGE_SIZE) + 2;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setCollectionsLoading(false);
        return;
      }
      const res = await getUserCenterThreads({
        uid: Number(uid),
        type: USER_CENTER_THREAD_TYPE.FAVORITE,
        page,
        page_size: USER_CENTER_PAGE_SIZE,
      });
      const patchedNewItems = (res.items || []).map((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return patch ? { ...item, ...patch } : item;
      });
      const validPatchedItems = patchedNewItems.filter((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return !(patch && patch.favorite === false);
      });
      const returnedCount = res.items?.length || 0;
      setCollectionsData((prev) => {
        const existingIds = new Set(prev.map((item) => String(item.tid)));
        const filteredNewItems = validPatchedItems.filter((item) => !existingIds.has(String(item.tid)));
        return [...prev, ...filteredNewItems];
      });
      setCollectionsInitialized(true);
      const nextCollectionsHasMore = returnedCount >= USER_CENTER_PAGE_SIZE;
      collectionsHasMoreRef.current = nextCollectionsHasMore;
      setCollectionsHasMore(nextCollectionsHasMore);
    } catch (error) {
      console.error("Failed to load more collections:", error);
    } finally {
      collectionsLoadInFlightRef.current = false;
      setCollectionsLoading(false);
    }
  }, [collectionsLoading, collectionsInitialized, collectionsData.length, displayAccount?.id, account?.id, threadStatePatchRef]);

  // 加载更多我的评论。
  const loadMoreReplies = useCallback(async () => {
    if (repliesLoading || !repliesHasMore || repliesRequestInFlightRef.current) return;
    setRepliesLoading(true);
    repliesRequestInFlightRef.current = true;
    try {
      const currentItems = repliesDataRef.current;
      const page = repliesPageRef.current;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setRepliesLoading(false);
        repliesRequestInFlightRef.current = false;
        return;
      }

      const res = (await getUserRepliesList({
        uid: Number(uid),
        page,
        pageSize: USER_CENTER_REPLY_PAGE_SIZE,
      })) as { items: IUserReplyItem[]; total_items: number };

      // 按接口固定字段：items / total_items
      const newItems = res.items || [];
      const total = res.total_items;

      const existingIds = new Set(currentItems.map((item) => String(item.pid)));
      const filteredNewItems = newItems.filter((item) => !existingIds.has(String(item.pid)));
      const mergedLength = currentItems.length + filteredNewItems.length;

      const mergedItems = [...currentItems, ...filteredNewItems];
      repliesDataRef.current = mergedItems;
      setRepliesData(mergedItems);

      setRepliesInitialized(true);
      setRepliesHasMore(filteredNewItems.length > 0 && mergedLength < total);

      // page-based：下一次直接取下一页。
      repliesPageRef.current = page + 1;
    } catch (error) {
      console.error("Failed to load more replies:", error);
    } finally {
      setRepliesLoading(false);
      repliesRequestInFlightRef.current = false;
    }
  }, [repliesLoading, repliesHasMore, displayAccount?.id, account?.id]);

  // 加载更多消息。
  const loadMoreMessages = useCallback(async () => {
    if (messagesLoading || !messagesHasMore || messageRefreshInFlightRef.current) return;
    setMessagesLoading(true);
    try {
      const currentItems = messagesDataRef.current;
      const offset = currentItems.length;
      const res = await getNotificationList({ offset, limit: USER_CENTER_MESSAGE_PAGE_SIZE });
      const newItems = res.items || [];
      const total = res.total_items;
      const existingIds = new Set(currentItems.map((item) => String(item.id)));
      const filteredNewItems = newItems.filter((item) => !existingIds.has(String(item.id)));
      const mergedLength = currentItems.length + filteredNewItems.length;
      setMessagesData((prev) => {
        const prevIds = new Set(prev.map((item) => String(item.id)));
        const appendItems = filteredNewItems.filter((item) => !prevIds.has(String(item.id)));
        return [...prev, ...appendItems];
      });
      setMessagesInitialized(true);
      setMessagesHasMore(typeof total === "number" ? mergedLength < total : filteredNewItems.length >= USER_CENTER_MESSAGE_PAGE_SIZE);
    } catch (error) {
      console.error("Failed to load more messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, [messagesLoading, messagesHasMore]);

  // 重新拉取消息第一页。
  const reloadMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const res = await getNotificationList({ offset: 0, limit: USER_CENTER_MESSAGE_PAGE_SIZE });
      const newItems = res.items || [];
      const total = res.total_items;
      setMessagesData(newItems);
      setMessagesHasMore(typeof total === "number" ? newItems.length < total : newItems.length >= USER_CENTER_MESSAGE_PAGE_SIZE);
    } catch (error) {
      console.error("Failed to reload messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // 刷新消息并将未读数本地重置为 0。
  const refreshMessagesAndResetUnread = useCallback(
    async (options?: { reloadList?: boolean }) => {
      if (!(account?.messages && account.messages > 0)) return;
      if (messageRefreshInFlightRef.current) return;
      messageRefreshInFlightRef.current = true;
      try {
        if (options?.reloadList) {
          await reloadMessages();
          setMessagesInitialized(true);
        }
        if (typeof window !== "undefined" && Number(account?.messages) > 0) {
          (window as any).__USER_CENTER_UNREAD_FORCE_SYNC__ = true;
        }
        dispatch({
          type: ActionTypeValues.UPDATE_ACCOUNT,
          namespace: namespace.app,
          payload: { ...account, messages: 0 },
        });
      } finally {
        messageRefreshInFlightRef.current = false;
      }
    },
    [account, dispatch, reloadMessages]
  );

  // 点赞/收藏成功后的本地 patch 同步。
  const handleItemUpdate = useCallback((item: IThreadLisItem, type: USER_ACTION_TYPE, newStatus: boolean) => {
    const patchId = String(item.tid);
    const existingPatch = threadStatePatchRef.current[patchId] || {};
    let newPraiseCount = item.praise || 0;
    let newFavCount = item.favtimes || 0;
    if (type === USER_ACTION_TYPE.PRAISE) {
      const currentPraise = existingPatch.praise !== undefined ? existingPatch.praise : item.praise || 0;
      newPraiseCount = newStatus ? currentPraise + 1 : Math.max(0, currentPraise - 1);
    } else {
      const currentFav = existingPatch.favtimes !== undefined ? existingPatch.favtimes : item.favtimes || 0;
      newFavCount = newStatus ? currentFav + 1 : Math.max(0, currentFav - 1);
    }
    const newPatch: Partial<IThreadLisItem> = {
      ...existingPatch,
      ...(type === USER_ACTION_TYPE.PRAISE ? { praised: newStatus, praise: newPraiseCount } : { favorite: newStatus, favtimes: newFavCount }),
    };
    threadStatePatchRef.current[patchId] = newPatch;
    const patchedItem = { ...item, ...existingPatch, ...newPatch };

    const updateList = (list: IThreadLisItem[], isTargetList: boolean) => {
      let found = false;
      let newList = list.map((i) => {
        if (String(i.tid) === patchId) {
          found = true;
          return { ...i, ...newPatch };
        }
        return i;
      });
      if (isTargetList) {
        if (newStatus && !found) {
          newList = [patchedItem, ...newList];
        } else if (!newStatus && found) {
          const shouldDeferRemoval =
            type === USER_ACTION_TYPE.PRAISE || type === USER_ACTION_TYPE.COLLECT;
          if (!shouldDeferRemoval) {
            newList = newList.filter((i) => String(i.tid) !== patchId);
          }
        }
      }
      return newList;
    };

    // 同步更新帖子/点赞/收藏三个列表。
    setPostsData((prev) => updateList(prev, false));
    setLikesData((prev) => updateList(prev, type === USER_ACTION_TYPE.PRAISE));
    setCollectionsData((prev) => updateList(prev, type === USER_ACTION_TYPE.COLLECT));
  }, [threadStatePatchRef]);

  // 草稿删除：从 postsData 中移除对应 tid 的项。
  const handleDraftDelete = useCallback((tid: number) => {
    setPostsData((prev) => prev.filter((item) => item.tid !== tid));
  }, []);

  // 点赞页切换时过滤被取消点赞的项。
  useEffect(() => {
    if (activeTab !== "myLikes") return;
    setLikesData((prev) => prev.filter((item) => threadStatePatchRef.current[String(item.tid)]?.praised !== false));
  }, [activeTab, threadStatePatchRef]);

  // 收藏页切换时过滤被取消收藏的项。
  useEffect(() => {
    if (activeTab !== "myCollections") return;
    setCollectionsData((prev) => prev.filter((item) => threadStatePatchRef.current[String(item.tid)]?.favorite !== false));
  }, [activeTab, threadStatePatchRef]);

  // 按 activeTab 懒加载对应列表。
  useEffect(() => {
    if (activeTab === "myPosts" && !postsInitialized && !postsLoading) loadMorePosts();
    if (activeTab === "myLikes" && !likesInitialized && !likesLoading) loadMoreLikes();
    if (activeTab === "myCollections" && !collectionsInitialized && !collectionsLoading) loadMoreCollections();
    if (activeTab === "myReplies" && !repliesInitialized && !repliesLoading) loadMoreReplies();
    if (activeTab === "messages" && !messagesInitialized && !messagesLoading) loadMoreMessages();
  }, [
    activeTab,
    postsInitialized,
    postsLoading,
    likesInitialized,
    likesLoading,
    collectionsInitialized,
    collectionsLoading,
    repliesInitialized,
    repliesLoading,
    messagesInitialized,
    messagesLoading,
    loadMorePosts,
    loadMoreLikes,
    loadMoreCollections,
    loadMoreReplies,
    loadMoreMessages,
  ]);

  // 本人进入消息 tab 且有未读时，自动刷新并重置未读。
  useEffect(() => {
    if (!isSelfView || activeTab !== "messages") return;
    if (!(account?.messages && account.messages > 0)) return;
    refreshMessagesAndResetUnread({ reloadList: true });
  }, [isSelfView, activeTab, account?.messages, refreshMessagesAndResetUnread]);

  /**
   * 触底自动加载：监听全局滚动容器的 scroll 事件。
   *
   * 兼容「#__next 自身滚动」与「文档级原生滚动」两种情况：
   * 读取滚动数据时同时考虑 #__next、window.pageYOffset、documentElement；
   * 事件监听同时绑定 window 与 #__next，谁滚都能收到。
   * 距底部 600px 内提前预加载，避免滚到列表中间才触发。
   * 首次 / 依赖变化后主动复检：内容不足一屏时自动补足。
   * 使用 requestAnimationFrame 做节流，避免高频 scroll 事件导致性能问题。
   */
  useEffect(() => {
    const targetTabs = ["myPosts", "myLikes", "myCollections", "myReplies", "messages"];

    const triggerLoad = () => {
      if (activeTab === "myPosts") loadMorePosts();
      else if (activeTab === "myLikes") loadMoreLikes();
      else if (activeTab === "myCollections") loadMoreCollections();
      else if (activeTab === "myReplies") loadMoreReplies();
      else if (activeTab === "messages") loadMoreMessages();
    };

    // 兼容「#__next 自身滚动」与「文档级原生滚动」两种情况
    const getMetrics = () => {
      const nextEl = document.getElementById("__next");
      const docEl = document.documentElement;
      const scrollTop =
        (nextEl && nextEl.scrollTop) || window.pageYOffset || docEl.scrollTop || 0;
      const clientHeight = window.innerHeight || docEl.clientHeight;
      const scrollHeight = Math.max(
        nextEl ? nextEl.scrollHeight : 0,
        docEl.scrollHeight,
        document.body.scrollHeight
      );
      return { scrollTop, clientHeight, scrollHeight };
    };

    const handleScroll = () => {
      if (!targetTabs.includes(activeTab)) return;
      const { scrollTop, clientHeight, scrollHeight } = getMetrics();
      // 距底部 600px 内提前预加载
      if (scrollHeight - scrollTop - clientHeight < 200) triggerLoad();
    };

    let frameId: number | null = null;
    const schedule = () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        handleScroll();
      });
    };

    // 关键：window 与 #__next 都监听，谁滚都能收到
    window.addEventListener("scroll", schedule, { passive: true });
    const nextEl = document.getElementById("__next");
    if (nextEl) nextEl.addEventListener("scroll", schedule, { passive: true });

    // 首次 / 依赖变化后主动复检：内容不足一屏时自动补足
    handleScroll();

    return () => {
      window.removeEventListener("scroll", schedule);
      if (nextEl) nextEl.removeEventListener("scroll", schedule);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [
    activeTab,
    loadMorePosts,
    loadMoreLikes,
    loadMoreCollections,
    loadMoreReplies,
    loadMoreMessages,
  ]);

  // 从 postsData 里拆分草稿与已发布列表，供页面渲染层使用。
  const draftsViewModel = useMemo(() => {
    const d = postsData?.filter((item) => (item as any).displayorder === -4) || [];
    const p = postsData?.filter((item) => (item as any).displayorder !== -4) || [];
    return {
      drafts: d,
      publishedPosts: p,
      showDrafts: isSelfView && d.length > 0,
      showPosts: p.length > 0,
    };
  }, [postsData, isSelfView]);

  // 返回页面层消费的状态与操作方法。
  return {
    postsData,
    postsLoading,
    postsHasMore,
    likesData,
    likesLoading,
    likesHasMore,
    likesInitialized,
    collectionsData,
    collectionsLoading,
    collectionsHasMore,
    collectionsInitialized,
    repliesData,
    repliesLoading,
    repliesHasMore,
    repliesInitialized,
    messagesData,
    messagesLoading,
    messagesHasMore,
    messagesInitialized,
    handleItemUpdate,
    handleDraftDelete,
    ...draftsViewModel,
  };
};
